import type { RefObject } from 'react';
import { useBoundElement } from './glassChrome';
import '@/styles/premium-slim-chrome.css';

const OVERFLOW_ROW_SELECTOR = [
  '.premium-slim-stats',
  '.premium-slim-actions',
  '.coach-operations-hero__button-row',
  '.forge-command-nav',
  '.ea-section-nav',
  '.journal-quiet__suite',
  '.system-settings-jumpbar',
  '.heatmap-kpi-chips',
  '.formation-suite__nav',
].join(',');

export function markSlimOverflowRows(root: ParentNode) {
  root.querySelectorAll(OVERFLOW_ROW_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const marked = node.classList.contains('is-overflowing');
    if (marked) node.classList.remove('is-overflowing');
    const overflows = node.scrollWidth > node.clientWidth + 1;
    node.classList.toggle('is-overflowing', overflows);
  });
}

export function bindSlimChrome(root: HTMLElement) {
  const main = root.querySelector('main.premium-app-content');
  const onScroll = () => {
    if (!main) return;
    root.classList.toggle('is-scrolled', main.scrollTop > 24);
  };
  const onFocusIn = (event: FocusEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest('[data-slim-search]')) {
      root.classList.add('is-searching');
    }
  };
  const onFocusOut = (event: FocusEvent) => {
    const next = event.relatedTarget;
    if (!(next instanceof Element) || !next.closest('[data-slim-search]')) {
      root.classList.remove('is-searching');
    }
  };
  const mark = () => markSlimOverflowRows(root);
  const resizeObserver = new ResizeObserver(mark);
  resizeObserver.observe(root);
  const mutationObserver = new MutationObserver(mark);
  mutationObserver.observe(root, { childList: true, subtree: true });
  main?.addEventListener('scroll', onScroll, { passive: true });
  root.addEventListener('focusin', onFocusIn);
  root.addEventListener('focusout', onFocusOut);
  onScroll();
  mark();
  return () => {
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    main?.removeEventListener('scroll', onScroll);
    root.removeEventListener('focusin', onFocusIn);
    root.removeEventListener('focusout', onFocusOut);
  };
}

// Rebinds when the shell element changes (the coach layout swaps its shell
// for the training shell and back without unmounting).
export function useSlimChrome(rootRef: RefObject<HTMLElement | null>) {
  useBoundElement(rootRef, bindSlimChrome);
}
